/**
 * Contextual one-time hints (owner decision C): a short prompt only at the moment it helps, and only the first
 * HINT_SHOWS times, then never again (the counts persist). Pure: the game feeds a HintContext per frame and shows
 * what `update` returns.
 */
import type { KeyValue } from './settings';

export type HintId = 'wheelie' | 'airLean' | 'brakeTurn' | 'stoppie';
export const HINT_IDS: readonly HintId[] = ['wheelie', 'airLean', 'brakeTurn', 'stoppie'];
export type InputDevice = 'keyboard' | 'gamepad' | 'touch';

/** Times each hint is shown at most, ever. */
export const HINT_SHOWS = 2;
/** s between any two hints (one thing at a time). */
export const HINT_GAP = 20;
export const HINTS_KEY = 'megamx.hints.v1';

export interface HintContext {
  /** The default (assisted) scheme: the trick hints only make sense there. */
  assist: boolean;
  speed: number;     // m/s
  airborne: boolean;
  airTime: number;   // s in the air so far
  pitch: number;     // rad, nose up +
  steer: number;     // raw intent
  throttle: number;
  brake: number;     // the strongest brake lever asked
  lean: number;      // raw lean intent (-1 back .. +1 forward)
  /** m/s the track ahead allows (the assist's plan), Infinity off a known line. */
  limit: number;
  /** The lean trick the assist rides now (0 none, 1 wheelie, 2 stoppie): one done is one learned, its hint retires. */
  trick?: number;
}

/** Glyphs for the hint texts' {back} {fwd} {gas} {brake}, per input device. */
export function hintGlyphs(device: InputDevice, labels: { gas: string; brake: string; stick: string }): Record<'back' | 'fwd' | 'gas' | 'brake', string> {
  if (device === 'gamepad') return { back: 'LS ↓', fwd: 'LS ↑', gas: 'RT', brake: 'LT' };
  if (device === 'touch') return { back: `${labels.stick} ↓`, fwd: `${labels.stick} ↑`, gas: labels.gas, brake: labels.brake };
  return { back: 'Q / ↓', fwd: 'E / ↑', gas: 'W', brake: 'S' };
}

export class HintCoach {
  private readonly seen: Record<HintId, number>;
  private readonly hold: Record<HintId, number> = { wheelie: 0, airLean: 0, brakeTurn: 0, stoppie: 0 };
  private gap = 2; // a moment into a ride before the first one

  constructor(private readonly store: KeyValue | null) {
    this.seen = { wheelie: 0, airLean: 0, brakeTurn: 0, stoppie: 0 };
    try {
      const raw = JSON.parse(store?.getItem(HINTS_KEY) ?? '{}') as Record<string, unknown>;
      for (const id of HINT_IDS) if (typeof raw[id] === 'number') this.seen[id] = raw[id] as number;
    } catch { /* blocked or broken storage: start fresh */ }
  }

  shown(id: HintId): number { return this.seen[id]; }

  /** A new ride: the warm-up gap again, nothing half-held. */
  restart(): void { this.gap = 2; for (const id of HINT_IDS) this.hold[id] = 0; }

  /** One frame; returns the hint to show now (and counts it), or null. */
  update(c: HintContext, dt: number): HintId | null {
    this.gap -= dt;
    const learned: HintId | null = c.trick === 1 ? 'wheelie' : c.trick === 2 ? 'stoppie' : null;
    if (learned && this.seen[learned] < HINT_SHOWS) { this.seen[learned] = HINT_SHOWS; this.save(); }
    const straight = Math.abs(c.steer) < 0.2;
    const ground = !c.airborne;
    const want: Record<HintId, boolean> = {
      // A long straight at low speed on the gas, not leaning yet.
      wheelie: c.assist && ground && straight && c.speed > 3 && c.speed < 14 && c.throttle > 0.5 && c.lean > -0.3 && c.limit > c.speed + 4,
      // In the air with the nose going down.
      airLean: c.airborne && c.airTime > 0.2 && c.pitch < -0.22 && c.lean > -0.3,
      // A turn coming that needs a lot less speed, and still on the gas.
      brakeTurn: ground && Number.isFinite(c.limit) && c.speed > c.limit + 3 && c.brake < 0.1 && c.throttle > 0.3,
      // Braking hard going straight at speed, weight not forward.
      stoppie: c.assist && ground && straight && c.speed > 9 && c.brake > 0.6 && c.lean < 0.3,
    };
    let pick: HintId | null = null;
    for (const id of HINT_IDS) {
      this.hold[id] = want[id] ? this.hold[id] + dt : 0;
      const needs = id === 'airLean' ? 0.05 : id === 'stoppie' ? 0.3 : id === 'brakeTurn' ? 0.2 : 1;
      if (!pick && this.seen[id] < HINT_SHOWS && this.hold[id] >= needs) pick = id;
    }
    if (!pick || this.gap > 0) return null;
    this.seen[pick]++;
    this.gap = HINT_GAP;
    this.hold[pick] = 0;
    this.save();
    return pick;
  }

  private save(): void {
    try { this.store?.setItem(HINTS_KEY, JSON.stringify(this.seen)); } catch { /* storage blocked: shown again next session */ }
  }
}
